import React, { useState, useRef } from 'react';
import { DestinationCreateRequest, PhotoDestination } from '../../types';
import { Plus, X, Upload, Loader2 } from 'lucide-react';
import { HtmlEditor } from '../common/HtmlEditor';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface CreateDestinationFormProps {
  onSubmit: (data: DestinationCreateRequest) => void;
  onCancel: () => void;
  loading: boolean;
}

export const CreateDestinationForm: React.FC<CreateDestinationFormProps> = ({
  onSubmit,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState<DestinationCreateRequest>({
    title: '',
    slug: '',
    summary: '',
    description: '',
    whyJoin: [],
    highlights: [],
    region: '',
    state: '',
    country: '',
    tags: [],
    published: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newWhyJoin, setNewWhyJoin] = useState('');
  const [newHighlight, setNewHighlight] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoAlt, setNewPhotoAlt] = useState('');
  const [newPhotoCaption, setNewPhotoCaption] = useState('');
  const [newPhotoCredit, setNewPhotoCredit] = useState('');
  const [newPhotoIsHero, setNewPhotoIsHero] = useState(false);
  const [newPhotoType, setNewPhotoType] = useState<'DESTINATION' | 'HOTEL' | 'VEHICLE' | 'OLD_TRIP'>('DESTINATION');
  const [newPhotoEntityId, setNewPhotoEntityId] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof DestinationCreateRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const addWhyJoin = () => {
    if (newWhyJoin.trim()) {
      setFormData(prev => ({
        ...prev,
        whyJoin: [...(prev.whyJoin || []), newWhyJoin.trim()],
      }));
      setNewWhyJoin('');
    }
  };

  const removeWhyJoin = (index: number) => {
    setFormData(prev => ({
      ...prev,
      whyJoin: prev.whyJoin?.filter((_, i) => i !== index) || [],
    }));
  };

  const addHighlight = () => {
    if (newHighlight.trim()) {
      setFormData(prev => ({
        ...prev,
        highlights: [...(prev.highlights || []), newHighlight.trim()],
      }));
      setNewHighlight('');
    }
  };

  const removeHighlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights?.filter((_, i) => i !== index) || [],
    }));
  };

  const addTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const removeTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || [],
    }));
  };

  // Photo management
  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      setUploadProgress(0);

      // Upload the file
      const result = await apiService.uploadMedia(file, (progress) => {
        setUploadProgress(progress);
      });

      // Get image dimensions
      const img = new Image();
      img.src = result.url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      // Create photo object with all fields
      const newPhoto: PhotoDestination = {
        url: result.url,
        alt: newPhotoAlt.trim() || '',
        caption: newPhotoCaption.trim() || '',
        credit: newPhotoCredit.trim() || '',
        isHero: newPhotoIsHero,
        type: newPhotoType,
        entityId: newPhotoEntityId.trim() || undefined,
        width: img.width,
        height: img.height,
        uploadedOn: Date.now(),
        tags: [],
        meta: {},
      };

      setFormData(prev => ({
        ...prev,
        photos: [...(prev.photos || []), newPhoto],
      }));

      // Reset form
      setNewPhotoUrl('');
      setNewPhotoAlt('');
      setNewPhotoCaption('');
      setNewPhotoCredit('');
      setNewPhotoIsHero(false);
      setNewPhotoType('DESTINATION');
      setNewPhotoEntityId('');
      setUploadProgress(0);
      toast.success('Photo uploaded successfully');

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addPhotoByUrl = () => {
    if (newPhotoUrl.trim()) {
      const newPhoto: PhotoDestination = {
        url: newPhotoUrl.trim(),
        alt: newPhotoAlt.trim() || '',
        caption: newPhotoCaption.trim() || '',
        credit: newPhotoCredit.trim() || '',
        isHero: newPhotoIsHero,
        type: newPhotoType,
        entityId: newPhotoEntityId.trim() || undefined,
        tags: [],
        meta: {},
      };
      setFormData(prev => ({
        ...prev,
        photos: [...(prev.photos || []), newPhoto],
      }));
      setNewPhotoUrl('');
      setNewPhotoAlt('');
      setNewPhotoCaption('');
      setNewPhotoCredit('');
      setNewPhotoIsHero(false);
      setNewPhotoType('DESTINATION');
      setNewPhotoEntityId('');
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos?.filter((_, i) => i !== index) || [],
    }));
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={onCancel}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4"
              disabled={loading}
            >
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Destinations
            </button>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create New Destination</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Add a new destination to the platform
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Basic Information
            </h3>

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter destination title (e.g., Manali)"
                disabled={loading}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Slug (optional - auto-generated if empty)
              </label>
              <input
                type="text"
                id="slug"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="manali-himachal-pradesh"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                SEO-friendly URL slug. Leave empty to auto-generate from title and state.
              </p>
            </div>

            <div>
              <label htmlFor="summary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Summary
              </label>
              <textarea
                id="summary"
                value={formData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Brief summary of the destination"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description (HTML)
              </label>
              <HtmlEditor
                value={formData.description || ''}
                onChange={(value) => handleInputChange('description', value)}
                placeholder="Enter detailed description of the destination (HTML supported)"
                disabled={loading}
                className="w-full"
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Location
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="India"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Himachal Pradesh"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Region
                </label>
                <input
                  type="text"
                  id="region"
                  value={formData.region}
                  onChange={(e) => handleInputChange('region', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="North India"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Why Join */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Why Join
            </h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newWhyJoin}
                onChange={(e) => setNewWhyJoin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addWhyJoin())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Add reason to visit"
                disabled={loading}
              />
              <button
                type="button"
                onClick={addWhyJoin}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {formData.whyJoin && formData.whyJoin.length > 0 && (
              <div className="space-y-2">
                {formData.whyJoin.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <span className="text-sm text-gray-900 dark:text-white">{item}</span>
                    <button
                      type="button"
                      onClick={() => removeWhyJoin(index)}
                      className="text-red-600 hover:text-red-800"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Highlights */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Highlights
            </h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newHighlight}
                onChange={(e) => setNewHighlight(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Add highlight"
                disabled={loading}
              />
              <button
                type="button"
                onClick={addHighlight}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {formData.highlights && formData.highlights.length > 0 && (
              <div className="space-y-2">
                {formData.highlights.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <span className="text-sm text-gray-900 dark:text-white">{item}</span>
                    <button
                      type="button"
                      onClick={() => removeHighlight(index)}
                      className="text-red-600 hover:text-red-800"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Photos
            </h3>
            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload Photo from Computer
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileSelect(file);
                    }
                  }}
                  className="hidden"
                  disabled={loading || uploadingPhoto}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || uploadingPhoto}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {uploadingPhoto ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <span className="text-gray-700 dark:text-gray-300">Uploading... {Math.round(uploadProgress)}%</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5 text-gray-500" />
                      <span className="text-gray-700 dark:text-gray-300">Choose Photo from Computer</span>
                    </>
                  )}
                </button>
                {uploadingPhoto && (
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">OR</span>
                </div>
              </div>

              {/* URL Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add Photo by URL
                </label>
                <input
                  type="url"
                  value={newPhotoUrl}
                  onChange={(e) => setNewPhotoUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="https://example.com/image.jpg"
                  disabled={loading}
                />
              </div>

              {/* Photo Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    value={newPhotoAlt}
                    onChange={(e) => setNewPhotoAlt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Image description for accessibility"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Photo Type
                  </label>
                  <select
                    value={newPhotoType}
                    onChange={(e) => setNewPhotoType(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    disabled={loading}
                  >
                    <option value="DESTINATION">Destination</option>
                    <option value="HOTEL">Hotel</option>
                    <option value="VEHICLE">Vehicle</option>
                    <option value="OLD_TRIP">Old Trip</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Caption
                </label>
                <input
                  type="text"
                  value={newPhotoCaption}
                  onChange={(e) => setNewPhotoCaption(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Photo caption"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Credit
                </label>
                <input
                  type="text"
                  value={newPhotoCredit}
                  onChange={(e) => setNewPhotoCredit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Photo credit/attribution"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Entity ID (optional)
                </label>
                <input
                  type="text"
                  value={newPhotoEntityId}
                  onChange={(e) => setNewPhotoEntityId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="hotelId / vehicleId / tripId"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Optional: Link photo to specific entity (hotel, vehicle, or trip)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newPhotoIsHero}
                  onChange={(e) => setNewPhotoIsHero(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={loading}
                />
                <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Set as hero image (for OG images)
                </label>
              </div>

              <button
                type="button"
                onClick={addPhotoByUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                disabled={loading || !newPhotoUrl.trim()}
              >
                <Plus className="h-5 w-5" />
                <span>Add Photo by URL</span>
              </button>
            </div>
            {formData.photos && formData.photos.length > 0 && (
              <div className="space-y-2 mt-4">
                {formData.photos.map((photo, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-start space-x-4">
                      <img 
                        src={photo.url} 
                        alt={photo.alt || ''} 
                        className="w-24 h-24 object-cover rounded flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate mb-1">
                              {photo.url}
                            </p>
                            {photo.alt && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Alt: {photo.alt}</p>
                            )}
                            {photo.caption && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Caption: {photo.caption}</p>
                            )}
                            {photo.credit && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Credit: {photo.credit}</p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-1">
                              {photo.type && (
                                <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                                  {photo.type}
                                </span>
                              )}
                              {photo.isHero && (
                                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                                  Hero
                                </span>
                              )}
                              {photo.width && photo.height && (
                                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded">
                                  {photo.width} × {photo.height}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="ml-2 text-red-600 hover:text-red-800 flex-shrink-0"
                            disabled={loading}
                            title="Remove photo"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Tags
            </h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Add tag"
                disabled={loading}
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      disabled={loading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Published */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => handleInputChange('published', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={loading}
            />
            <label htmlFor="published" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Publish destination
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Destination'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

